/* eslint-disable semi */
import store from '@/store';
import { ApiClient, SasUtils, SpringConverter } from '@/core/libs';
import { saveAs } from 'file-saver';

const debug = process.env.NODE_ENV === 'development';

const STATUS_CANCELED = '000';
const STATUS_SUCCESS = '1';
const STATUS_CONFIRMATION_REQUIRED = '100';
// const STATUS_VALIDATION_ERROR = '300';
// const STATUS_EXCLUSIVE_ERROR = '310';
// const STATUS_DATA_NOT_FOUND_ERROR = '320';
// const STATUS_LOGICAL_ERROR = '400';
const STATUS_SYSTEM_ERROR = '500';
const STATUS_AUTH_REQUIRED = '800';
const STATUS_AUTH_ERROR = '810';
const STATUS_CSRF_ERROR = '820';
const STATUS_ERROR = '999';

/**
 * BlobからJSONへ変換する処理
 * 例)
 * blobToJson(blob).then(json => {}).catch(reason => {});
 */
function blobToJson (blob) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onerror = () => {
      fileReader.abort();
      reject(new Error('Fail to read blob.'));
    };
    fileReader.onload = () => {
      resolve(JSON.parse(fileReader.result));
    };
    fileReader.readAsText(blob);
  });
}

/**
 * API通信デフォルトファクトリ
 *
 * アプリ固有の通信処理はここで実装します
 * ・baseURLなどの基本設定
 * ・リクエスト送信時の処理
 * ・エラー時の処理
 *
 * アプリ固有の設定として下記を追加しています。
 * これらは、postやget時のconfig項目として指定します。
 * app {
 *  critical: エラー時にエラー画面へ遷移する(デフォルト false)
 *  through: 共通処理を行わないステータス指定(デフォルト 無し)
 *  silence: エラー時の通知有無の制御(デフォルト false)
 *  bind: エラーの格納先エラー変数の定義(デフォルト { errors: 'errors' })
 * }
 *
 * 基本的に、例外系の処理は共通化によって省略し、
 * 正常系の処理は呼び出し元で明示的に実装する、という方針です。
 */
export default function (owner, options) {
  // API_BASE_URLが"//"で開始しない場合は、現在アクセス中のホスト名+API_BASE_URLをベースURLとする。
  let baseURL = process.env.API_BASE_URL;
  if (!baseURL.match('^//')) {
    baseURL = '//' + location.host + baseURL;
  }
  // 開発環境等でスマホで閲覧する場合はbaseURLを参照するようにしてください
  // baseURL = baseURL.replace('//localhost:', 'http://' + location.hostname + ':');
  const api = new ApiClient({
    baseURL: process.env.API_BASE_URL
    // baseURL: baseURL// process.env.API_BASE_URL
  });

  /**
   * 送信時フック
   */
  api.addBeforeHook(config => {
    config.app = config.app || {};

    if (debug) {
      const options = {
        method: config.method,
        url: config.url,
        app: config.app
      };
      console.debug(`app options: ${JSON.stringify(options)}`);
    }

    // リクエストに認証情報を付与する
    if ('XSRF_HEADER_NAME' in process.env) {
      config.withCredentials = true;
      config.xsrfHeaderName = process.env.XSRF_HEADER_NAME;
      const $store = store();
      if ($store) {
        const csrf = $store.getters.csrf;
        if (csrf) {
          config.headers.common[process.env.XSRF_HEADER_NAME] = csrf.token;
        }
      }
    }

    // 元データを退避する
    config.app.data = ('data' in config) ? config.data : null;
    config.app.params = ('params' in config) ? config.params : null;

    // データ変換とヘッダ補正
    if (config.app.upload) {
      // multipartヘッダ付与
      // アップロード時はSpring形式への変換は行わない
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      // 送信形式:JSON または フォーム形式
      // APIサーバ側でJSON形式対応の場合はデフォルト値としてtrueを指定してください。
      // APIサーバ側がJSON形式非対応の場合はデフォルト値としてfalseを指定してください。
      let sendByJson = true;
      if ('json' in config.app) {
        sendByJson = config.app.json;
      }

      // GETパラメータをSpring形式に変換する
      config.paramsSerializer = (params) => SpringConverter.stringifyParams(params);
      // JSON形式でない場合はPOSTパラメータをSpring形式に変換する
      if (sendByJson) {
        // NULL値を空文字に変換する処理を実施
        let null2EmptyString = true;
        if ('null2EmptyString' in config.app) {
          null2EmptyString = config.app.null2EmptyString;
        }
        if (null2EmptyString && config.data !== null && config.data !== undefined) {
          config.data = SpringConverter.null2EmptyString(config.data);
        }
      } else {
        // Spring形式のFORMデータに変換
        if ('data' in config && (typeof config.data !== 'string')) {
          config.data = SpringConverter.stringifyData(config.data);
        }
      }
    }

    // ------------------------------------------------------------------------
    // ダウンロード機能の場合はresponseTypeにblobを指定
    // フック処理でjson形式に変換またはファイル保存処理を実行する必要あり
    // ------------------------------------------------------------------------
    if (config.app.download) {
      if (debug) {
        console.debug('Download mode before hook');
      }
      config.responseType = 'blob';
    }

    return config;
  });

  /**
   * 応答時フック:1 ファイルダウンロード処理対応
   */
  api.addAfterHook(promise => {
    return promise
      .then(response => {
        // ダウンロードモードでない場合はそのまま後続処理
        if (!response.config.app.download) {
          return response;
        }

        if (debug) {
          console.debug('Download mode after hook');
        }

        // レスポンスデータをBlobに変換
        const blob = new Blob([response.data], {
          type: response.data.type
        });

        // レスポンスヘッダに
        // content-dispositionが指定されている場合はファイルダウンロード処理実行
        // content-dispositionが指定されていない場合はBlobからJSON形式に変換して後続処理へ
        if ('content-disposition' in response.headers) {
          // ファイル保存処理を実行してデフォルトの成功結果を生成して後続処理
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'DownloadFile';
          if (contentDisposition.match(/filename\*=utf-8''/i)) {
            // RFC-6266形式対応
            fileName = contentDisposition.replace(/.*filename\*=utf-8''(.+)/i, '$1');
            fileName = decodeURI(fileName);
          } else if (contentDisposition.match(/.*filename="([^"]+)"/i)) {
            // 非RFC-6266形式対応
            fileName = contentDisposition.replace(/.*"([^"]+)".*/i, '$1')
          }
          saveAs(blob, fileName);
          response.data = {
            status: STATUS_SUCCESS,
            results: {
              fileName: fileName
            }
          }
          return response;
        } else {
          // ファイルデータ以外（確認・エラー系のJSONデータ）が返却された場合
          // レスポンスをblobからjsonに変換して後続処理へ引き渡す
          return blobToJson(blob).then(json => {
            response.data = json;
            return response;
          })
        }
      });
  });

  /**
   * 応答時フック:2 レスポンスの定型化処理
   *
   * ステータスが未定義の場合は下記のステータスを定義する
   * STATUS_CANCELED: 通信キャンセル時のステータス
   * STATUS_ERROR:    デフォルトのエラーステータス
   */
  api.addAfterHook(promise => {
    return promise
      .then(response => {
        if (response.data && response.data.status === STATUS_SUCCESS) {
          return response;
        } else {
          const reason = {
            config: response.config,
            response: response
          };
          throw reason;
        }
      })
      .catch(reason => {
        const canceled = api.isCanceled(reason);

        reason = reason || {};

        // 通信キャンセル判定用のフラグを追加
        reason.isCanceled = canceled;

        // reason.responseオブジェクトの補正
        reason.response = reason.response || {};
        if (typeof reason.response !== 'object') {
          reason._response = reason.response;
          reason.response = {};
        }

        // reason.response.dataオブジェクトの補正
        reason.response.data = reason.response.data || {};
        if (typeof reason.response.data !== 'object') {
          reason.response._data = reason.response.data;
          reason.response.data = {};
        }

        // エラーコードを補完
        reason.response.data.status = String(reason.response.data.status || (canceled ? STATUS_CANCELED : STATUS_ERROR));

        // 500:システムエラーの場合のメッセージ補正
        // BusinessExceptionである場合はメッセージとして設定する。
        if (reason.response.data.status === STATUS_SYSTEM_ERROR) {
          if ('exception' in reason.response.data) {
            if (reason.response.data.exception.match(/BusinessException$/)) {
              reason.response.data.messages = [reason.response.data.message];
            }
          }
        }

        throw reason;
      });
  });

  /**
   * 応答時フック:3 エラーコードによる共通処理
   */
  api.addAfterHook(promise => {
    return promise
      .catch(reason => {
        // 呼び出し元がVueでない場合はこの時点で終了
        if (!SasUtils.isVueComponent(owner)) {
          throw reason;
        }

        // 通信キャンセル時はこの時点で終了
        if (reason.isCanceled) {
          throw reason;
        }

        // エラーとエラーコード
        const data = reason.response.data;

        // アプリ固有の設定項目
        // 通知設定
        // クリティカル指定
        // 共通処理を行わないエラーコード指定
        // エラーの格納先エラー変数名
        const appConfig = reason.config.app || {};
        const silence = appConfig.silence;
        const critical = appConfig.critical;
        const through = appConfig.through || [];
        const bind = appConfig.bind || {
          errors: 'errors'
        };

        // 送信時に退避したデータを戻す
        if ('data' in appConfig) {
          reason.config.data = appConfig.data;
        }
        if ('params' in appConfig) {
          reason.config.params = appConfig.params;
        }

        // 指定されたエラーコードについてはこの時点で終了
        const throughCodeList = Array.isArray(through) ? through : [through];
        if (throughCodeList.some(val => val === '*' || val === data.status)) {
          throw reason;
        }

        // エラーデータをコンポーネントの変数に格納
        Object.keys(bind)
          .filter(key => key in data)
          .forEach(key => {
            owner[bind[key]] = data[key];
          });

        // メッセージ文字列
        const message = (data.messages) ? (data.messages.join('\n')) : owner.$t(`api.${data.status}`);

        // 特定の処理を要するエラーのハンドリング
        // 1) 認証系エラー: ログイン画面へ遷移
        //  ログイン画面へ遷移する(ログイン後は現在の画面へ遷移します)
        // 2) クリティカル判定
        //  エラー画面へ遷移する
        //  pushで遷移すると、戻るボタンで再度エラー画面へ遷移することになるので、replaceで遷移します。
        // 3) STATUS_CONFIRMATION_REQUIRED: 再確認処理
        //  再確認が必要な処理の場合は定型の再確認処理を行う
        //  3-1) 確認画面を表示する
        //  3-2) リクエストパラメータのconfirm項目に1を設定して再度送信
        //  再確認でNOが選択された場合は元の通信エラーを再スローする
        //  （呼び出し元のcatchで処理される）
        //  再確認でYESが選択された場合は再度API通信を行いそのPromiseを返却する
        //  （通信結果が呼び出し元のthenまたはcatchで処理される）
        if ([STATUS_AUTH_REQUIRED, STATUS_AUTH_ERROR, STATUS_CSRF_ERROR].includes(data.status)) {
          // ログインページへ遷移
          owner.$_app_notify.error(message);
          owner.$router.push({
            name: 'LOGIN'
            // query: {
            //  redirect: owner.$route.fullPath
            // }
          });
        } else if (data.status === STATUS_CONFIRMATION_REQUIRED) {
          // 確認再実行
          return owner
            .$_app_confirm(message)
            .catch(() => {
              throw reason;
            })
            .then(() => {
              // 確認フラグを設定して再送する
              // 確認フラグはPOSTの場合でもクエリパラメータとして送信する
              reason.config.params = reason.config.params || {};
              reason.config.params.confirm = 1;
              return api.request(reason.config);
            });
        } else if (critical) {
          // クリティカル指定時はエラーページへ遷移
          owner.$_app_notify.error(message);
          owner.$router.replace({
            name: 'U999999',
            query: {
              status: data.status
            }
          });
        } else if (!silence) {
          // エラーメッセージ通知
          owner.$_app_notify.error(message);
        }

        throw reason;
      });
  });

  return api;
}



// WEBPACK FOOTER //
// ./src/api/factories/app.js